import React, { Component } from "react";
import { Card, CardBody, CardHeader, Button } from "reactstrap";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import swal from "sweetalert2";
import TableContainerWithLocation from "../Components/TableContainerWithLocation";
import recovery from "@/api/recovery";
import { dateFormatter } from "@/util/formatter";
import { handleError } from "@/util/errorHandler";
import KeyRecoveryModal from "@/views/Components/KeyRecoveryRequest";
import { Link } from "react-router-dom";

class KeyRecoveryRequests extends Component {
  state = {
    isKeyRecoveryOpen: false,
    accountId: "",
    fullName: "",
  };

  callback = null;

  renderActionButton = (cell, row) => {
    const rowID = row.date + row.id;
    let actionName = "";
    let isCancellable = false;

    switch (row.status) {
      case "OPENED":
      case "VERIFIED":
      case "PREPARED":
      case "READY":
        actionName = "Cancel";
        isCancellable = true;
        break;
      case "SENT":
        actionName = "Revoke";
        isCancellable = true;
        break;
      default:
        break;
    }

    const buttons = [];

    if (actionName) {
      buttons.push(
        <Button
          key={rowID}
          color="primary"
          disabled={!isCancellable}
          onClick={() => this.askCancelConfirmation(row, actionName)}>
          {actionName}
        </Button>,
      );
    }

    if (row.status === "CREATED") {
      buttons.push(
        <Button
          color="success"
          key={rowID}
          onClick={() => this.askVerify(row.accountId)}>
          Verify
        </Button>,
      );
    }

    if (row.status === "VERIFIED") {
      buttons.push(
        <Button
          color="success"
          key={row.id}
          onClick={() => this.askCompleteRequest(row)}>
          Complete request
        </Button>,
      );
    }

    return <>{buttons.length ? buttons : "No actions"}</>;
  };

  askCompleteRequest = row => {
    this.setState({
      accountId: row.accountId,
      fullName: row.name,
    });

    this.onToggleKeyRecoveryModal();
  };

  onToggleKeyRecoveryModal = () => {
    this.setState(({ isKeyRecoveryOpen }) => ({
      isKeyRecoveryOpen: !isKeyRecoveryOpen,
    }));
  };

  askVerify = async (id, callback = null) => {
    this.callback = callback;
    const html = `
      <span>
        Do you want to verify this request?
      </span>
      <br/>
      <span>Please, enter code in the input below.</span>
    `;

    await swal({
      title: `Verify request?`,
      html,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      input: "text",
      preConfirm: async code => {
        await recovery.verifyAccountRecoveryRequest({ accountId: id, code });
        if (this.callback) this.callback();
        this.table && this.table.loadAndStoreData();
      },
    });
  };

  askCancelConfirmation = async (row, actionName, callback = null) => {
    this.callback = callback;
    const html = `
      <span>
        Do you want to ${actionName.toLowerCase()} key recovery request for account ID
        <strong>${row.accountId}</strong>?
      </span>
      <br/>
      <span>Please, enter the reason in the input below.</span>
    `;

    await swal({
      title: `${actionName} request?`,
      html,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      input: "text",
      preConfirm: async reason => {
        await this.onConfirmRevoke(row, reason);
        if (this.callback) this.callback();
        this.table && this.table.loadAndStoreData();
      },
    });
  };

  onConfirmRevoke = async ({ trxHash, id, status }, reason) => {
    try {
      if (status === "SENT") {
        await recovery.revokeByTx(trxHash, reason);
      } else {
        await recovery.revokeById(id, reason);
      }

      await swal("Success", "Recovery request is cancelled", "success");
      this.table && this.table.loadAndStoreData();
    } catch (err) {
      handleError(err);
    }
  };

  reloadPage = () => {
    setImmediate(() => {
      this.table.loadAndStoreData();
    });
  };

  table = null;

  bindInnerTableRef = innerComponent => {
    this.table = innerComponent;
  };

  viewFormatter = (cell, row) => (
    <Link
      to={{
        pathname: `/key_recovery_requests_info/${row.id}`,
        state: { data: row },
      }}>
      <div>
        <i className="fa fa-search" style={{ color: "#D15050" }} />
      </div>
    </Link>
  );

  render() {
    const { accountId, fullName } = this.state;
    return (
      <Card>
        <CardHeader>Key recovery requests list</CardHeader>
        <CardBody>
          <TableContainerWithLocation
            loadData={recovery.fetchRecoveryRequestsList}
            options={{
              defaultSortName: "date", // to change default sort order also change default arguments in API recovery.fetchRecoveryRequestsList
              defaultSortOrder: "desc",
            }}
            wrappedComponentRef={this.bindInnerTableRef}>
            <BootstrapTable
              version="4"
              search
              pagination
              striped
              hover
              condensed
              remote
              keyField="id">
              <TableHeaderColumn
                dataFormat={this.viewFormatter}
                dataField="id"
                width="60"
                dataAlign="center">
                View
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataField="status"
                dataAlign="center"
                width="100">
                Status
              </TableHeaderColumn>
              <TableHeaderColumn dataSort dataField="accountId" width="150">
                Account ID
              </TableHeaderColumn>
              <TableHeaderColumn dataSort dataField="name" width="160">
                Name
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={trxHash => trxHash || "not created"}
                dataField="trxHash"
                width="100">
                Transaction hash
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={trxStatus => trxStatus || "not created"}
                dataField="trxStatus"
                width="100">
                TX status
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={dateFormatter}
                dataField="date"
                width="160">
                Registered Date
              </TableHeaderColumn>
              <TableHeaderColumn
                dataSort
                dataFormat={dateFormatter}
                dataField="updateTime"
                width="130">
                Last update
              </TableHeaderColumn>
              <TableHeaderColumn width="100" dataField="comment">
                Reason
              </TableHeaderColumn>
              <TableHeaderColumn
                dataFormat={this.renderActionButton}
                width="300"
                dataAlign="center">
                Actions
              </TableHeaderColumn>
            </BootstrapTable>
          </TableContainerWithLocation>
        </CardBody>

        <KeyRecoveryModal
          isOpen={this.state.isKeyRecoveryOpen}
          isKeyRecovery={true}
          onModalClose={this.onToggleKeyRecoveryModal}
          userData={{ accountId, fullName }}
          onLoadTableData={this.reloadPage}
        />
      </Card>
    );
  }
}

export default KeyRecoveryRequests;
